import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import {
  FormSelect,
  FormText,
  PaginationNavButton,
  PaginationPageButton,
  ShipmentBulkUploadAccordionRow,
} from '..'
import {
  ShipmentGroup,
  ShipmentGroupErrors,
  ShipmentPieceRow,
} from '../../interfaces/Import/ShipmentGroupInterface'
import { DestinationInterface } from '../../interfaces/Account/DestinationInterface'
import { ServiceDTO } from '../../interfaces/Services/Services'

const pageSizes = [5, 10, 20, 50]

export const ShipmentBulkUploadAccordionList: FC<{
  groups: ShipmentGroup[]
  errors: ShipmentGroupErrors[]
  identificationTypes: any[]
  typeShipment: { value: string; name: string }[]
  businessUnits: any[]
  cities: any[]
  citiesByBu?: any[]
  pieceCategories: any[]
  isCOD: boolean
  serviceOptions?: ServiceDTO[]
  onChangeGroup: (index: number, patch: Partial<ShipmentGroup>) => void
  onChangePiece: (
    groupIndex: number,
    pieceIndex: number,
    patch: Partial<ShipmentPieceRow>
  ) => void
  onAddPiece: (groupIndex: number) => void
  onRemovePiece: (groupIndex: number, pieceIndex: number) => void
  validatePiece: (
    p: ShipmentPieceRow
  ) => Partial<Record<keyof ShipmentPieceRow, string>>
  onRequestMerge?: (index: number) => void
  phonePrefixList?: { value: string; text: string }[]
  favoriteDestinations?: DestinationInterface[]
  favsByRif?: Map<string, DestinationInterface>
}> = ({
  groups,
  errors,
  identificationTypes,
  typeShipment,
  businessUnits,
  cities,
  citiesByBu = [],
  pieceCategories,
  isCOD,
  serviceOptions = [],
  onChangeGroup,
  onChangePiece,
  onAddPiece,
  onRemovePiece,
  validatePiece,
  onRequestMerge,
  phonePrefixList = [],
  favoriteDestinations = [],
  favsByRif,
}) => {
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(10)
  const [query, setQuery] = useState('')
  const [openId, setOpenId] = useState<string | null>(null)

  const normalized = (s: string) => (s || '').toString().toLowerCase()

  const matchesQuery = useCallback((g: ShipmentGroup, q: string) => {
    if (!q) return true
    const fields = [
      g.trackingNumber,
      g.consigneeName,
      g.consigneeDocNumber,
      g.consigneePhone,
      g.destinationAddress,
      g.storeCode,
      g.destinationCity,
    ]
    return fields.some((v) => normalized(String(v || '')).includes(q))
  }, [])

  const computeOrderedIds = useCallback((): string[] => {
    const pairs = groups.map((g, i) => ({ g, i }))
    const rank = (pair: { g: ShipmentGroup; i: number }) => {
      if (pair.g.coordWarning) return 1
      if (
        Object.keys(errors[pair.i] || {}).length > 0 ||
        pair.g.isDuplicateInDB
      )
        return 0
      return 2
    }
    return [...pairs]
      .sort((a, b) => {
        const ra = rank(a)
        const rb = rank(b)
        if (ra !== rb) return ra - rb
        return a.g.trackingNumber.localeCompare(b.g.trackingNumber, undefined, {
          numeric: true,
          sensitivity: 'base',
        })
      })
      .map((p) => p.g.id)
  }, [groups, errors])

  const [orderedIds, setOrderedIds] = useState<string[]>(computeOrderedIds)

  useEffect(() => {
    setOrderedIds((prev) => {
      const live = new Set(groups.map((g) => g.id))
      const kept = prev.filter((id) => live.has(id))
      const known = new Set(kept)
      const added = groups.filter((g) => !known.has(g.id)).map((g) => g.id)
      if (added.length === 0 && kept.length === prev.length) return prev
      return [...kept, ...added]
    })
    setOpenId((prev) =>
      prev && groups.some((g) => g.id === prev) ? prev : null
    )
  }, [groups])

  useEffect(() => {
    if (openId !== null) return
    const next = computeOrderedIds()
    setOrderedIds((prev) =>
      prev.length === next.length && prev.every((id, i) => id === next[i])
        ? prev
        : next
    )
  }, [computeOrderedIds, openId])

  const idToIndex = useMemo(() => {
    const m = new Map<string, number>()
    groups.forEach((g, i) => m.set(g.id, i))
    return m
  }, [groups])

  const filteredRows = useMemo(() => {
    const q = normalized(query)
    const rows: { g: ShipmentGroup; i: number }[] = []
    for (const id of orderedIds) {
      const i = idToIndex.get(id)
      if (i == null) continue
      const g = groups[i]
      if (matchesQuery(g, q)) rows.push({ g, i })
    }
    return rows
  }, [orderedIds, idToIndex, groups, query, matchesQuery])

  const total = filteredRows.length
  const totalPages = Math.max(1, Math.ceil(total / size))
  const safePage = Math.min(page, totalPages - 1)
  const start = safePage * size
  const end = Math.min(start + size, total)
  const pageRows = useMemo(
    () => filteredRows.slice(start, end),
    [filteredRows, start, end]
  )

  const commitReorder = useCallback(
    (groupId: string) => {
      const newIds = computeOrderedIds()
      setOrderedIds(newIds)
      const q = normalized(query)
      const visible = newIds.filter((id) => {
        const i = idToIndex.get(id)
        return i != null && matchesQuery(groups[i], q)
      })
      const pos = visible.indexOf(groupId)
      if (pos < 0) return
      setPage(Math.floor(pos / size))
      setTimeout(() => {
        document
          .getElementById(`bulk-row-${groupId}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 60)
    },
    [computeOrderedIds, query, idToIndex, groups, size, matchesQuery]
  )

  const handleToggle = useCallback(
    (groupId: string) => {
      commitReorder(groupId)
      setOpenId((prev) => (prev === groupId ? null : groupId))
    },
    [commitReorder]
  )

  const windowSize = totalPages > 5 ? 4 : totalPages
  let windowStart = safePage
  if (windowStart + windowSize > totalPages)
    windowStart = Math.max(0, totalPages - windowSize)
  const pagesWindow = Array.from(
    { length: windowSize },
    (_, i) => windowStart + i
  )

  return (
    <div className="w-full">
      <div className="flex items-end justify-between mb-3 gap-3">
        <FormSelect<number>
          id="shipment-v2-page-size"
          name="shipment-v2-page-size"
          label=""
          selected={size}
          options={pageSizes}
          optionString={(opt) => `${opt}/pág`}
          onSelectOption={(opt) => {
            setSize(opt)
            setPage(0)
          }}
          className="!h-8"
          containerClassname="w-40"
        />
        <div className="flex-1 max-w-md">
          <FormText
            id="shipment-v2-search"
            name="shipment-v2-search"
            label="Buscar"
            placeholder="Nº guía, destinatario…"
            value={query}
            onChange={(e: any) => {
              setQuery(e.target.value)
              setPage(0)
            }}
            className="!h-8"
          />
        </div>
      </div>

      {pageRows.map(({ g, i }) => (
        <div key={g.id} id={`bulk-row-${g.id}`}>
          <ShipmentBulkUploadAccordionRow
            group={groups[i]}
            index={i}
            open={openId === g.id}
            onToggle={() => handleToggle(g.id)}
            errors={errors[i] || {}}
            identificationTypes={identificationTypes}
            typeShipment={typeShipment}
            businessUnits={businessUnits}
            cities={cities}
            citiesByBu={citiesByBu}
            pieceCategories={pieceCategories}
            isCOD={isCOD}
            serviceOptions={serviceOptions}
            onChangeGroup={(patch) => onChangeGroup(i, patch)}
            onChangePiece={(pIdx, patch) => onChangePiece(i, pIdx, patch)}
            onAddPiece={() => onAddPiece(i)}
            onRemovePiece={(pIdx) => onRemovePiece(i, pIdx)}
            validatePiece={validatePiece}
            onRequestMerge={
              onRequestMerge ? () => onRequestMerge(i) : undefined
            }
            phonePrefixList={phonePrefixList}
            favoriteDestinations={favoriteDestinations}
            favsByRif={favsByRif}
          />
        </div>
      ))}

      <div className="flex items-center justify-between mt-3 text-sm">
        <div>
          Página {safePage + 1} de {totalPages}
        </div>
        <div className="flex items-center gap-2">
          <PaginationNavButton
            direction="prev"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={safePage === 0}
          />
          {pagesWindow.map((p) => (
            <PaginationPageButton
              key={p}
              page={p + 1}
              active={p === safePage}
              onClick={() => setPage(p)}
            />
          ))}
          <PaginationNavButton
            direction="next"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={safePage >= totalPages - 1}
          />
        </div>
      </div>
    </div>
  )
}
